var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import { jsx as _jsx, Fragment as _Fragment, jsxs as _jsxs } from "react/jsx-runtime";
import { useState, useMemo } from 'react';
import { AlphafiVault, convertToKAndM, NotificationComp } from 'ui-kit';
import { useCurrentAccount, useSignAndExecuteTransaction } from '@mysten/dapp-kit';
import { useSubscription, useEventSubscription } from '../hooks';
import { useBalances } from '../context/BalanceContext';
import { useNetworkString } from '../context/NetworkContext';
import { getIcon } from '../utils/getIcon';
import { getImage } from '../utils/getImage';
import { transactionHandler } from '../utils/transactionHandlers';
import { getDecimalPlaces } from '../utils/currencyUtils';
import { getButtonState } from '../utils/buttonStateUtils';
import { withdrawAlphaTxb } from 'alphafi-sdk';
import { getSuiClient } from '../utils/client';
import { validateNetwork } from '../utils/networkUtils';
import BigNumber from 'bignumber.js';
import { webSocketService } from '../services/websocket';
const AlphaVaultpage = ({ walletConnectFunctionality, themeName, isMobile, }) => {
    const currentAccount = useCurrentAccount();
    const { walletBalancesMap } = useBalances();
    const network = useNetworkString();
    const { mutate: signAndExecuteTransaction } = useSignAndExecuteTransaction();
    // Alpha vault pool ID (hardcoded as provided)
    const ALPHA_VAULT_POOL_ID = '0x6ee8f60226edf48772f81e5986994745dae249c2605a5b12de6602ef1b05b0c1';
    // Websocket subscriptions for real-time data
    const { data: staticData } = useEventSubscription('system', 'static_data');
    const { data: protocolData } = useSubscription('protocol');
    const { data: portfolioData } = useSubscription((currentAccount === null || currentAccount === void 0 ? void 0 : currentAccount.address) ? `portfolio:${currentAccount.address}` : null);
    const { data: _poolDetailsData } = useSubscription(`pool_details:${ALPHA_VAULT_POOL_ID}`);
    const { data: coinInfoData } = useSubscription('coin_info');
    // State for token input values and notifications
    const [tokenValues, setTokenValues] = useState({});
    const [isTransactionLoading, setIsTransactionLoading] = useState(false);
    const [notificationInfo, setNotificationInfo] = useState({
        title: '',
        description: '',
        spinner: false,
        link: '',
        image: undefined,
    });
    const [isNotificationOpen, setIsNotificationOpen] = useState(false);
    const [isMaxClicked, setIsMaxClicked] = useState(false);
    const [activeTabIndex, setActiveTabIndex] = useState(0);
    // Alpha vault static data
    const alphaVaultStatic = useMemo(() => {
        console.log('staticData', staticData);
        return staticData === null || staticData === void 0 ? void 0 : staticData.find((pool) => pool.pool_id === ALPHA_VAULT_POOL_ID);
    }, [staticData]);
    // Alpha vault protocol data
    const alphaVaultData = useMemo(() => {
        if (!(protocolData === null || protocolData === void 0 ? void 0 : protocolData.pools_data))
            return null;
        console.log('protocolData', protocolData.pools_data);
        return protocolData.pools_data[ALPHA_VAULT_POOL_ID] || null;
    }, [protocolData]);
    // Portfolio balance for alpha vault
    const alphaVaultBalance = useMemo(() => {
        if (!(portfolioData === null || portfolioData === void 0 ? void 0 : portfolioData.pool_balances))
            return null;
        console.log('portfolioData', portfolioData.pool_balances);
        return portfolioData.pool_balances[ALPHA_VAULT_POOL_ID] || null;
    }, [portfolioData]);
    // Alpha token data
    const alphaTokenData = useMemo(() => {
        var _a;
        if (!((_a = alphaVaultStatic === null || alphaVaultStatic === void 0 ? void 0 : alphaVaultStatic.asset_types) === null || _a === void 0 ? void 0 : _a[0]))
            return null;
        console.log('alphaVaultStatic', alphaVaultStatic);
        const alphaAsset = alphaVaultStatic.asset_types[0];
        const alphaTokenType = alphaAsset.asset_type;
        const alphaSymbol = alphaAsset.asset_symbol;
        const coinInfo = coinInfoData === null || coinInfoData === void 0 ? void 0 : coinInfoData[alphaTokenType];
        const alphaPrice = (coinInfo === null || coinInfo === void 0 ? void 0 : coinInfo.price) || '0';
        const decimals = Number((coinInfo === null || coinInfo === void 0 ? void 0 : coinInfo.decimals) || 9);
        return {
            symbol: alphaSymbol,
            name: 'Alpha Token',
            icon: getIcon('alpha'),
            walletBalance: activeTabIndex === 0
                ? (parseFloat((walletBalancesMap === null || walletBalancesMap === void 0 ? void 0 : walletBalancesMap.get(alphaTokenType)) || '0') / Math.pow(10, decimals)).toString()
                : (() => {
                    var _a;
                    const poolBalance = ALPHA_VAULT_POOL_ID
                        ? (_a = portfolioData === null || portfolioData === void 0 ? void 0 : portfolioData.pool_balances) === null || _a === void 0 ? void 0 : _a[ALPHA_VAULT_POOL_ID]
                        : undefined;
                    if (!poolBalance)
                        return '0';
                    if ('balances' in poolBalance && poolBalance.balances) {
                        const tokenBalance = poolBalance.balances.find((balance) => balance.label.toUpperCase() === alphaSymbol.toUpperCase());
                        if (!tokenBalance && alphaSymbol.toUpperCase() === 'ALPHA') {
                            const lockedBalance = poolBalance.balances.find((b) => b.label === 'alpha_locked');
                            const unlockedBalance = poolBalance.balances.find((b) => b.label === 'alpha_unlocked');
                            const locked = parseFloat((lockedBalance === null || lockedBalance === void 0 ? void 0 : lockedBalance.value) || '0');
                            const unlockedRaw = parseFloat((unlockedBalance === null || unlockedBalance === void 0 ? void 0 : unlockedBalance.value) || '0');
                            const unlocked = unlockedRaw;
                            const totalBalance = locked + unlocked;
                            // Only return balance if it's a valid positive number
                            if (totalBalance > 0 && isFinite(totalBalance)) {
                                return totalBalance.toString();
                            }
                            return '0';
                        }
                        return (tokenBalance === null || tokenBalance === void 0 ? void 0 : tokenBalance.value) || '0';
                    }
                    return '0';
                })(),
            value: tokenValues[alphaSymbol] || '',
            price: alphaPrice,
            decimals,
            tokenType: alphaTokenType,
        };
    }, [
        alphaVaultStatic,
        walletBalancesMap,
        coinInfoData,
        tokenValues,
        activeTabIndex,
        portfolioData,
    ]);
    // Get SUI balance for gas fees
    const suiBalance = useMemo(() => {
        const suiTokenType = '0x2::sui::SUI';
        const balance = (walletBalancesMap === null || walletBalancesMap === void 0 ? void 0 : walletBalancesMap.get(suiTokenType)) || '0';
        return (parseFloat(balance) / Math.pow(10, 9)).toString();
    }, [walletBalancesMap]);
    // Collect rewards handler
    //   const handleCollectRewards = useCallback(async () => {
    //     if (!currentAccount?.address) return;
    //     try {
    //       await transactionHandler({
    //         poolName: alphaVaultStatic?.pool_name || 'Alpha Vault',
    //         amount: '0', // Not needed for claim
    //         poolId: ALPHA_VAULT_POOL_ID,
    //         decimals: '9',
    //         network: network,
    //         action: 'claim',
    //         modalState: {
    //           isOpen: true,
    //           token: alphaTokenData,
    //           actionType: 'claim',
    //         },
    //         address: currentAccount.address,
    //         signAndExecuteTransaction: signAndExecuteTransaction,
    //         setNotificationInfo: setNotificationInfo,
    //         setIsNotificationOpen: setIsNotificationOpen,
    //         setIsTransactionLoading: setIsTransactionLoading,
    //         displayAmount: `${parseFloat(portfolioData?.alpha_rewards_to_claim || '0').toFixed(4)} ALPHA`,
    //         suiBalance: suiBalance,
    //         resetInputValues: () => {},
    //       });
    //     } catch (error) {
    //       console.error('Collect rewards failed:', error);
    //     }
    //   }, [
    //     currentAccount,
    //     alphaVaultStatic,
    //     alphaTokenData,
    //     network,
    //     signAndExecuteTransaction,
    //     portfolioData,
    //     suiBalance,
    //   ]);
    // Stats cards data
    const statsCards = useMemo(() => {
        // TODO: Need to verify with pratik for sui rewards
        // const pendingRewards = portfolioData?.alpha_rewards_to_claim || '0';
        var _a, _b;
        // Calculate total staked amount (alpha_locked + alpha_unlocked)
        let totalStakedAlpha = 0;
        let stakedAmount = '0';
        if (alphaVaultBalance && 'balances' in alphaVaultBalance && alphaVaultBalance.balances) {
            const lockedBalance = alphaVaultBalance.balances.find((b) => b.label === 'alpha_locked');
            const unlockedBalance = alphaVaultBalance.balances.find((b) => b.label === 'alpha_unlocked');
            const locked = parseFloat((lockedBalance === null || lockedBalance === void 0 ? void 0 : lockedBalance.value) || '0');
            const unlockedRaw = parseFloat((unlockedBalance === null || unlockedBalance === void 0 ? void 0 : unlockedBalance.value) || '0');
            // Get ALPHA token decimals and price
            const alphaAsset = (_a = alphaVaultStatic === null || alphaVaultStatic === void 0 ? void 0 : alphaVaultStatic.asset_types) === null || _a === void 0 ? void 0 : _a[0];
            const alphaTokenType = alphaAsset === null || alphaAsset === void 0 ? void 0 : alphaAsset.asset_type;
            const decimals = Number(((_b = coinInfoData === null || coinInfoData === void 0 ? void 0 : coinInfoData[alphaTokenType || '']) === null || _b === void 0 ? void 0 : _b.decimals) || 9);
            // Convert unlocked from raw value to proper decimal format
            const unlocked = unlockedRaw / Math.pow(10, decimals);
            // Total staked ALPHA tokens
            totalStakedAlpha = locked + unlocked;
            // Calculate USD value
            stakedAmount = totalStakedAlpha.toString();
        }
        // const apr = alphaVaultData?.apy || '0';
        return {
            //   pendingRewards: {
            //     stats: [
            //       {
            //         value: `${convertToKAndM(pendingRewards)}`,
            //         alphaIcon: getIcon('sui'),
            //         collectButton: parseFloat(pendingRewards) > 0,
            //         handleCollectButtonClick: async () => {
            //           await handleCollectRewards();
            //         },
            //       },
            //     ],
            //     isAlphaVault: true,
            //   },
            stakedAmount: {
                stats: [
                    {
                        value: `${convertToKAndM(stakedAmount)}`,
                        label: 'Staked Amount',
                        alphaIcon: getIcon('alpha'),
                    },
                ],
                isAlphaVault: true,
            },
            apr: {
                stats: [
                    {
                        value: `20%`,
                        label: 'Expected APR',
                    },
                ],
                isAlphaVault: true,
            },
            tvl: {
                stats: [
                    {
                        value: `$${convertToKAndM((alphaVaultData === null || alphaVaultData === void 0 ? void 0 : alphaVaultData.tvl) || '0')}`,
                        label: 'Vault TVL',
                    },
                ],
                isAlphaVault: true,
            },
        };
    }, [
        // portfolioData,
        alphaVaultBalance,
        alphaVaultData,
        // handleCollectRewards,
        alphaVaultStatic,
        coinInfoData,
    ]);
    // Handlers
    const handleTabChange = (tabIndex) => {
        setActiveTabIndex(tabIndex);
        setTokenValues({});
        setIsMaxClicked(false);
    };
    const handleTokenValueChange = (tokenSymbol, value) => {
        setTokenValues((prev) => (Object.assign(Object.assign({}, prev), { [tokenSymbol]: value })));
        setIsMaxClicked(false);
    };
    const handleMaxClick = (tokenSymbol) => {
        if (alphaTokenData && tokenSymbol === alphaTokenData.symbol) {
            const rawBalance = alphaTokenData.walletBalance;
            const balance = parseFloat(rawBalance);
            // Check if balance is valid
            if (isNaN(balance) || !isFinite(balance) || balance <= 0) {
                return;
            }
            // Truncate to reasonable precision (remove floating point errors without rounding)
            const decimalPlaces = getDecimalPlaces(tokenSymbol);
            const multiplier = Math.pow(10, decimalPlaces);
            const maxValue = (Math.floor(balance * multiplier) / multiplier).toString();
            handleTokenValueChange(tokenSymbol, maxValue);
            setIsMaxClicked(true);
        }
    };
    const handleDeposit = () => __awaiter(void 0, void 0, void 0, function* () {
        if (!(currentAccount === null || currentAccount === void 0 ? void 0 : currentAccount.address) || !alphaTokenData || !tokenValues[alphaTokenData.symbol])
            return;
        const amount = tokenValues[alphaTokenData.symbol];
        const formattedAmount = `${parseFloat(amount).toFixed(getDecimalPlaces(alphaTokenData.symbol))} ${alphaTokenData.symbol}`;
        try {
            yield transactionHandler({
                poolName: (alphaVaultStatic === null || alphaVaultStatic === void 0 ? void 0 : alphaVaultStatic.pool_name) || 'Alpha Vault',
                amount: amount,
                poolId: ALPHA_VAULT_POOL_ID,
                decimals: String(alphaTokenData.decimals),
                network: network,
                action: 'deposit',
                modalState: {
                    isOpen: true,
                    token: alphaTokenData,
                    actionType: 'deposit',
                },
                address: currentAccount.address,
                signAndExecuteTransaction: signAndExecuteTransaction,
                setNotificationInfo: setNotificationInfo,
                setIsNotificationOpen: setIsNotificationOpen,
                setIsTransactionLoading: setIsTransactionLoading,
                displayAmount: formattedAmount,
                suiBalance: suiBalance,
                isAmountA: true, // Single asset pool
                resetInputValues: () => {
                    setTokenValues({});
                    setIsMaxClicked(false);
                },
            });
        }
        catch (error) {
            console.error('Deposit failed:', error);
        }
    });
    const handleWithdraw = () => __awaiter(void 0, void 0, void 0, function* () {
        if (!(currentAccount === null || currentAccount === void 0 ? void 0 : currentAccount.address) || !alphaTokenData || !tokenValues[alphaTokenData.symbol])
            return;
        const amount = tokenValues[alphaTokenData.symbol];
        const formattedAmount = `${parseFloat(amount).toFixed(getDecimalPlaces(alphaTokenData.symbol))} ${alphaTokenData.symbol}`;
        // Show notification
        setNotificationInfo({
            title: 'Awaiting Response',
            description: 'Requesting wallet to approve transaction.',
            spinner: true,
            link: '',
            image: undefined,
        });
        setIsNotificationOpen(true);
        setIsTransactionLoading(true);
        try {
            if (!currentAccount)
                return;
            // Convert amount to proper format with decimals (same as AirdropPage)
            let withdrawAmount = new BigNumber(parseFloat(amount) * Math.pow(10, alphaTokenData.decimals)).toString();
            withdrawAmount = withdrawAmount.split('.')[0]; // Remove any decimal part
            const transactionBlock = yield withdrawAlphaTxb(withdrawAmount, true, // withdrawFromLocked: true means withdrawing from locked balance along with unlocked balance
            currentAccount.address);
            const validatedNetwork = validateNetwork(network);
            const suiClient = getSuiClient(validatedNetwork);
            signAndExecuteTransaction({ transaction: transactionBlock }, {
                onSuccess: (result) => __awaiter(void 0, void 0, void 0, function* () {
                    var _a, _b;
                    const txbCheck = yield suiClient.waitForTransaction({
                        digest: result.digest,
                        options: { showEffects: true },
                    });
                    const isSuccess = ((_b = (_a = txbCheck.effects) === null || _a === void 0 ? void 0 : _a.status) === null || _b === void 0 ? void 0 : _b.status) === 'success';
                    if (isSuccess) {
                        setNotificationInfo({
                            image: getIcon('success'),
                            title: 'Withdraw Successful',
                            description: `You have successfully withdrawn ${formattedAmount}.`,
                            spinner: false,
                            link: `https://suiscan.xyz/mainnet/tx/${result.digest}`,
                        });
                        setTokenValues({});
                        setIsMaxClicked(false);
                        webSocketService.sendTransactionNotification(ALPHA_VAULT_POOL_ID, currentAccount.address);
                    }
                    else {
                        setNotificationInfo({
                            image: getIcon('failed'),
                            title: 'Withdraw Failed',
                            description: 'Transaction failed. Please try again.',
                            spinner: false,
                            link: `https://suiscan.xyz/mainnet/tx/${result.digest}`,
                        });
                    }
                    setIsNotificationOpen(true);
                    setIsTransactionLoading(false);
                }),
                onError() {
                    setNotificationInfo({
                        image: getIcon('failed'),
                        title: 'Withdraw Failed',
                        description: 'Transaction was rejected or failed.',
                        spinner: false,
                        link: '',
                    });
                    setIsNotificationOpen(true);
                    setIsTransactionLoading(false);
                },
            });
        }
        catch (error) {
            console.error('Withdraw failed:', error);
            setNotificationInfo({
                image: getIcon('failed'),
                title: 'Withdraw Failed',
                description: error instanceof Error ? error.message : 'An error occurred during withdrawal.',
                spinner: false,
                link: '',
            });
            setIsNotificationOpen(true);
            setIsTransactionLoading(false);
        }
    });
    // Button state calculation
    const buttonState = useMemo(() => {
        if (!alphaTokenData)
            return { disabled: true, text: 'Deposit' };
        return getButtonState(tokenValues, activeTabIndex, [alphaTokenData], isMaxClicked);
    }, [tokenValues, activeTabIndex, alphaTokenData, isMaxClicked]);
    // Balance text based on active tab
    const balanceText = useMemo(() => {
        return activeTabIndex === 0 ? 'In Wallet:' : 'In Vault:';
    }, [activeTabIndex]);
    return (_jsxs(_Fragment, { children: [_jsx(AlphafiVault, { title: "All-New ALPHA Vault", description: "", apr: parseFloat((alphaVaultData === null || alphaVaultData === void 0 ? void 0 : alphaVaultData.apy) || '0'), buttonText: buttonState.text, walletConnectFunctionality: walletConnectFunctionality, isWalletConnected: !!(currentAccount === null || currentAccount === void 0 ? void 0 : currentAccount.address), tokens: alphaTokenData ? [alphaTokenData] : [], depositPeriod: "Instant", withdrawalPeriod: "7 days", statsCards: statsCards, onDeposit: handleDeposit, onWithdraw: handleWithdraw, onTokenValueChange: handleTokenValueChange, onMaxClick: handleMaxClick, onTabChange: handleTabChange, isLoading: isTransactionLoading, isButtonDisabled: buttonState.disabled, balanceText: balanceText, backgroundImage: themeName === 'dark' && !isMobile
                    ? getImage('airdropFirstBgDarkWeb')
                    : themeName !== 'dark' && !isMobile
                        ? getImage('airdropFirstBgLightWeb')
                        : themeName === 'dark' && isMobile
                            ? getImage('airdropFirstBgLightMobile')
                            : getImage('airdropFirstBgLightMobile') }), _jsx(NotificationComp, { isOpen: isNotificationOpen, setIsOpen: setIsNotificationOpen, info: notificationInfo })] }));
};
export default AlphaVaultpage;
